import type { TemplateContext, ParsedVariable, FallbackItem } from "./types";
import { getValueByPath } from "@tellescope/utils";

/**
 * Regex to match the full template pattern including chained fallbacks.
 * Supports:
 * - {{source.field}} - basic interpolation
 * - {{source.field||"fallback"}} - fallback with string literal
 * - {{source.field||source2.field2}} - fallback with field reference
 * - {{source.field||source2.field2||"literal"}} - chained fallbacks ending with literal
 * - {{source.field||source2.field2||source3.field3}} - chained field fallbacks
 * - {{source.field?"text"}} - conditional operator (truthy only)
 * - {{source.field?"truthy":"falsy"}} - ternary operator (truthy and falsy)
 *
 * This regex matches the entire template variable including all fallbacks.
 */
const TEMPLATE_REGEX =
  /\{\{(\w+)\.(\w+(?:\.\w+)*)((?:\|\|(?:"[^"]*"|\w+\.\w+(?:\.\w+)*))*)?(\?"([^"]*)"(?::"([^"]*)")?)??\}\}/g;

/**
 * Regex to parse individual fallback items from the fallback chain.
 * Matches either "literal" or source.field
 */
const FALLBACK_ITEM_REGEX = /\|\|(?:"([^"]*)"|(\w+)\.(\w+(?:\.\w+)*))/g;

/**
 * Check if a value is considered "missing" for conditional operators.
 * Missing: undefined, null, empty string, NaN
 * NOT missing: 0, false, any non-empty string
 */
export function isMissing(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === "string" && value === "") return true;
  if (typeof value === "number" && Number.isNaN(value)) return true;
  return false;
}

/**
 * Parse fallback chain from the captured fallback string.
 * Returns an array of FallbackItem objects.
 */
function parseFallbackChain(fallbackChain: string): FallbackItem[] {
  const items: FallbackItem[] = [];
  const regex = new RegExp(FALLBACK_ITEM_REGEX.source, "g");
  let match: RegExpExecArray | null;

  while ((match = regex.exec(fallbackChain)) !== null) {
    if (match[1] !== undefined) {
      // String literal fallback: ||"text"
      items.push({ type: "literal", value: match[1] });
    } else if (match[2] && match[3]) {
      // Field reference fallback: ||source.field
      items.push({ type: "field", value: "", source: match[2], field: match[3] });
    }
  }

  return items;
}

/**
 * Parse all template variables from a string.
 */
export function parseTemplateVariables(text: string): ParsedVariable[] {
  const variables: ParsedVariable[] = [];
  const regex = new RegExp(TEMPLATE_REGEX.source, "g");
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    const source = match[1] ?? "";
    const field = match[2] ?? "";
    const variable: ParsedVariable = {
      source,
      field,
      raw: match[0],
    };

    // Check for fallback chain: ||...
    const fallbackChain = match[3];
    if (fallbackChain) {
      variable.operator = "fallback";
      const fallbacks = parseFallbackChain(fallbackChain);
      variable.fallbacks = fallbacks;

      // For backwards compatibility, set operatorText if single string literal
      const firstFallback = fallbacks[0];
      if (fallbacks.length === 1 && firstFallback?.type === "literal") {
        variable.operatorText = firstFallback.value;
      }
    }

    // Check for conditional/ternary operator: ?"text" or ?"truthy":"falsy"
    const conditionalMatch = match[4];
    if (conditionalMatch) {
      variable.operator = "conditional";
      variable.operatorText = match[5];
      // Check for falsy text (ternary): :"falsy"
      if (match[6] !== undefined) {
        variable.falsyText = match[6];
      }
    }

    variables.push(variable);
  }

  return variables;
}

/**
 * Check if a string contains any template variables.
 */
export function hasTemplateVariables(text: string | undefined | null): boolean {
  if (!text) return false;
  const regex = new RegExp(TEMPLATE_REGEX.source, "g");
  return regex.test(text);
}

/**
 * Get a field value from the enduser, with fallback to custom fields.
 * If the field doesn't exist at the top level, check enduser.fields[fieldName].
 */
function getEnduserFieldValue(field: string, enduser: unknown): unknown {
  // First try the direct path
  const { value: directValue } = getValueByPath(enduser, field);
  if (directValue !== undefined) {
    return directValue;
  }

  // If not found and this is a top-level field (no dots), check fields object
  if (!field.includes(".")) {
    const { value: fieldsValue } = getValueByPath(enduser, `fields.${field}`);
    if (fieldsValue !== undefined) {
      return fieldsValue;
    }
  }

  return undefined;
}

/**
 * Get a field value from a source, applying source-specific logic.
 * For enduser source, falls back to custom fields if not found at top level.
 */
function getSourceFieldValue(source: string, field: string, context: TemplateContext): unknown {
  switch (source) {
    case "enduser":
      if (!context.enduser) return undefined;
      return getEnduserFieldValue(field, context.enduser);
    case "organization":
      if (!context.organization) return undefined;
      return getValueByPath(context.organization, field).value;
    case "snippet":
      return context.snippets?.[field];
    default:
      return undefined;
  }
}

/**
 * Interpolate all template variables in a string.
 * Returns the string with variables replaced by their values.
 * Missing/invalid variables are replaced with empty string.
 *
 * Supports chained fallbacks: {{a.b||c.d||e.f||"literal"}}
 * Each field is tried in order until a non-missing value is found.
 *
 * For enduser source, if a field is not found at the top level,
 * it will fall back to checking enduser.fields[fieldName] for custom fields.
 */
export function interpolateText(text: string | undefined | null, context: TemplateContext): string {
  if (!text) return "";

  const regex = new RegExp(TEMPLATE_REGEX.source, "g");
  return text.replace(regex, (_match, source, field, fallbackChain, conditionalMatch, conditionalText, falsyText) => {
    // Try the primary field first
    const value = getSourceFieldValue(source, field, context);

    // Conditional/ternary operator: {{source.field?"text"}} or {{source.field?"truthy":"falsy"}}
    if (conditionalMatch) {
      if (isMissing(value)) {
        // Value is missing - return falsy text if provided, otherwise empty string
        return falsyText !== undefined ? falsyText : "";
      }
      return conditionalText;
    }

    // If primary value is not missing, use it
    if (!isMissing(value)) {
      return String(value);
    }

    // If there's a fallback chain, process it
    if (fallbackChain) {
      const fallbacks = parseFallbackChain(fallbackChain);

      for (const fallback of fallbacks) {
        if (fallback.type === "literal") {
          // String literal - always use it as final fallback
          return fallback.value;
        } else if (fallback.type === "field" && fallback.source && fallback.field) {
          // Field reference - try to get its value
          const fallbackValue = getSourceFieldValue(fallback.source, fallback.field, context);
          if (!isMissing(fallbackValue)) {
            return String(fallbackValue);
          }
        }
      }
    }

    // No fallback matched or no fallback provided
    return "";
  });
}

/**
 * Extract unique data sources from a string.
 * Useful for lazy data fetching.
 */
export function extractTemplateSources(text: string): Set<string> {
  const sources = new Set<string>();
  const variables = parseTemplateVariables(text);
  for (const v of variables) {
    sources.add(v.source);
  }
  return sources;
}
